"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

type ServiceDeferredBlockProps = {
	children: ReactNode;
	placeholder?: ReactNode;
	rootMargin?: string;
	className?: string;
};

export function ServiceBlockPlaceholder({
	className,
}: {
	className?: string;
}) {
	return (
		<div
			className={cn("min-h-64 animate-pulse rounded-2xl bg-secondary/5", className)}
			aria-hidden
		/>
	);
}

export default function ServiceDeferredBlock({
	children,
	placeholder,
	rootMargin = "800px 0px",
	className,
}: ServiceDeferredBlockProps) {
	const ref = useRef<HTMLDivElement>(null);
	const [shouldRender, setShouldRender] = useState(false);

	useEffect(() => {
		if (shouldRender) return;
		const node = ref.current;
		if (!node) return;

		if (!("IntersectionObserver" in window)) {
			setShouldRender(true);
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setShouldRender(true);
					observer.disconnect();
				}
			},
			{ rootMargin },
		);

		observer.observe(node);
		return () => observer.disconnect();
	}, [rootMargin, shouldRender]);

	return (
		<div ref={ref} className={className}>
			{shouldRender ? children : (placeholder ?? <ServiceBlockPlaceholder />)}
		</div>
	);
}
